import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card, Flex } from "antd";
import "@/helpers.css";
import "./styles.css";

export type RadioOption<T extends string = string> = {
    value: T;
    label: React.ReactNode;
    imageSrc: string;
    imageAlt?: string;
    description?: string;
    disabled?: boolean;
};

export type RadioGroupProps<T extends string = string> = {
    options: RadioOption<T>[];

    value: T | null;
    defaultValue?: T;
    onChange: (value: T) => void;

    name?: string;
    disabled?: boolean;
};

function chunkItems(items: RadioOption[], chunkSize: number): RadioOption[][] {
    if (chunkSize <= 0) {
        return [items];
    }

    const rows: RadioOption[][] = [];
    for (let i = 0; i < items.length; i += chunkSize) {
        rows.push(items.slice(i, i + chunkSize));
    }
    return rows;
}

function getItemsPerRow(width: number) {
    if (width > 600) {
        return 4;
    }
    if (width > 400) {
        return 3;
    }
    if (width > 200) {
        return 2;
    }
    return 1;
}

function RadioGroupCard(props: RadioGroupProps) {
    const { options, onChange, value, defaultValue, name = "radio-group-card", disabled } = props;
    const [currentSelection, setSelection] = useState<RadioOption["value"] | null>(value ?? defaultValue ?? null);
    const [itemsPerRow, setItemsPerRow] = useState(4);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const selectValue = (val: RadioOption["value"]) => {
        const didValueChange = val !== currentSelection;
        if (!didValueChange) {
            return;
        }

        setSelection(val);
        onChange(val);
    };

    const displayValue = value ?? currentSelection;
    const rows = useMemo(() => chunkItems(options, itemsPerRow), [options, itemsPerRow]);

    useEffect(() => {
        setSelection(value ?? defaultValue ?? null);
    }, [value, defaultValue]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return undefined;
        }

        const updateItemsPerRow = () => {
            const width = container.getBoundingClientRect().width;
            const nextValue = getItemsPerRow(width);
            setItemsPerRow((prevValue) => (prevValue === nextValue ? prevValue : nextValue));
        };

        updateItemsPerRow();

        if (typeof ResizeObserver !== "undefined") {
            const observer = new ResizeObserver(updateItemsPerRow);
            observer.observe(container);
            return () => observer.disconnect();
        }

        window.addEventListener("resize", updateItemsPerRow);
        return () => window.removeEventListener("resize", updateItemsPerRow);
    }, []);

    return (
        <Flex ref={containerRef} flex="auto" className="radio-group-card" vertical>
            {rows.map((row, rowIndex) => (
                <Flex key={`row-${rowIndex}`} className="radio-group-card-row" justify="space-evenly">
                    {row.map((item) => {
                        const inputId = `${name}-${item.value}`;
                        const isSelected = displayValue === item.value;
                        const isDisabled = disabled || item.disabled;

                        return (
                            <div key={item.value} className="radio-group-card-cell">
                                <label className="radio-group-card-label" htmlFor={inputId}>
                                    <input
                                        className="radio-group-card-input"
                                        type="radio"
                                        id={inputId}
                                        name={name}
                                        value={item.value}
                                        checked={isSelected}
                                        disabled={isDisabled}
                                        onChange={() => {
                                            if (!isDisabled) {
                                                selectValue(item.value);
                                            }
                                        }}
                                    />
                                    <Card
                                        hoverable
                                        variant="outlined"
                                        className={`radio-group-card-item ${isSelected ? "item-selected" : ""}`}
                                    >
                                        <Flex align="center" wrap="wrap" vertical>
                                            <div className="top-line" />
                                            <div className="radio-group-card-icon">
                                                <img alt={item.imageAlt} src={item.imageSrc} loading="lazy" />
                                            </div>
                                            <p className="font-weight-semibold font-size-xs -0 radio-group-card-text">
                                                {item.label}
                                            </p>
                                            {item.description ? (
                                                <p className="radio-group-card-description">{item.description}</p>
                                            ) : null}
                                        </Flex>
                                    </Card>
                                </label>
                            </div>
                        );
                    })}
                </Flex>
            ))}
        </Flex>
    );
}

export default RadioGroupCard;
